// src/apps/secret/composables/useAttachments.ts
//
// Orchestrates the E2EE attachment flow on top of the crypto service and the
// pre-sign API. The app server never touches the bytes: the browser encrypts,
// uploads ciphertext straight to object storage with a pre-signed PUT, and the
// recipient downloads ciphertext straight from storage with a one-time GET and
// decrypts locally. The encryption key lives ONLY in the link fragment.
//
// Fragment format (never sent to the server): `#att=<id>:<key>,<id>:<key>`.
// The id→key mapping is explicit (not positional), so it does not depend on any
// server-side ordering. The filename/type are plaintext server metadata; only
// the file CONTENT is encrypted.

import { encryptFile, decryptFile } from '@/services/e2ee.service';
import { useApi } from '@/shared/composables/useApi';

export interface UploadedAttachment {
  id: string;
  filename: string;
  size: number; // ciphertext size
  fragmentKey: string; // AES key, base64url — goes in the link fragment only
}

export interface AttachmentSummary {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  state: string;
}

/** One id:key pair recovered from the link fragment. */
export interface FragmentEntry {
  id: string;
  key: string;
}

type ProgressFn = (loaded: number, total: number) => void;

const FRAGMENT_PREFIX = 'att=';

/** Build the link-fragment payload that carries the per-attachment keys. */
export function buildAttachmentFragment(uploaded: UploadedAttachment[]): string {
  if (!uploaded.length) return '';
  return FRAGMENT_PREFIX + uploaded.map((a) => `${a.id}:${a.fragmentKey}`).join(',');
}

/** Parse `att=id:key,id:key` out of a raw URL fragment (with or without `#`). */
export function parseAttachmentFragment(rawFragment: string): FragmentEntry[] {
  const frag = rawFragment.replace(/^#/, '');
  const part = frag
    .split('&')
    .find((p) => p.startsWith(FRAGMENT_PREFIX));
  if (!part) return [];
  return part
    .slice(FRAGMENT_PREFIX.length)
    .split(',')
    .map((pair) => {
      const idx = pair.indexOf(':');
      return idx === -1
        ? { id: pair, key: '' }
        : { id: pair.slice(0, idx), key: pair.slice(idx + 1) };
    })
    .filter((e) => e.id && e.key);
}

// PUT/GET the ciphertext directly to/from object storage with a pre-signed URL,
// reporting byte progress. Uses XHR because fetch() has no upload-progress event.
function xhrTransfer(
  method: 'PUT' | 'GET',
  url: string,
  opts: { body?: ArrayBuffer | null; onProgress?: ProgressFn } = {}
): Promise<ArrayBuffer> {
  const { body = null, onProgress } = opts;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.responseType = 'arraybuffer';
    const track = method === 'PUT' ? xhr.upload : xhr;
    if (onProgress) {
      track.onprogress = (e: ProgressEvent) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as ArrayBuffer);
      else reject(new Error(`Storage ${method} failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error(`Storage ${method} network error`));
    xhr.send(body);
  });
}

export function useAttachments() {
  const $api = useApi();

  const base = (isGuest: boolean) => `/api/v3${isGuest ? '/guest' : ''}`;

  /**
   * Encrypt a file and upload its ciphertext for a receipt. Returns the id +
   * fragment key the link needs. `onProgress` reports upload byte progress.
   */
  async function uploadAttachment(
    receiptId: string,
    file: File,
    opts: { isGuest?: boolean; onProgress?: ProgressFn } = {}
  ): Promise<UploadedAttachment> {
    const isGuest = !!opts.isGuest;
    const { ciphertext, fragmentKey } = await encryptFile(file);

    // Register the attachment; the server returns its id + a pre-signed PUT.
    const { data } = await $api.post(`${base(isGuest)}/receipt/${receiptId}/attachment`, {
      size: ciphertext.byteLength,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
    });
    const record = data?.record ?? {};
    const uploadUrl: string = data?.details?.upload_url;
    if (!uploadUrl) throw new Error('No upload URL returned');

    await xhrTransfer('PUT', uploadUrl, { body: ciphertext, onProgress: opts.onProgress });

    return {
      id: record.id,
      filename: record.filename ?? file.name,
      size: ciphertext.byteLength,
      fragmentKey,
    };
  }

  /** List a secret's attachments (filenames/sizes) for display on the reveal. */
  async function listAttachments(
    secretId: string,
    isGuest = false
  ): Promise<AttachmentSummary[]> {
    const { data } = await $api.get(`${base(isGuest)}/secret/${secretId}/attachments`);
    return (data?.record?.attachments ?? []) as AttachmentSummary[];
  }

  /**
   * Download + decrypt one attachment (one-time), then discard it from storage.
   * Returns a Blob + filename ready to save. Consuming (presign-download) is a
   * one-shot: the server refuses a second call.
   */
  async function downloadAttachment(
    entry: FragmentEntry,
    opts: { isGuest?: boolean; onProgress?: ProgressFn } = {}
  ): Promise<{ blob: Blob; filename: string }> {
    const isGuest = !!opts.isGuest;
    const { data } = await $api.post(`${base(isGuest)}/attachment/${entry.id}/download`, {});
    const downloadUrl: string = data?.details?.download_url;
    const filename: string = data?.details?.filename || 'download';
    const contentType: string = data?.details?.content_type || 'application/octet-stream';
    if (!downloadUrl) throw new Error('This attachment is no longer available');

    const ciphertext = await xhrTransfer('GET', downloadUrl, { onProgress: opts.onProgress });
    const bytes = await decryptFile(entry.key, ciphertext);

    // Best-effort immediate cleanup so the ciphertext does not linger in storage.
    // Failure here is non-fatal (burn + lifecycle backstop it).
    try {
      await $api.post(`${base(isGuest)}/attachment/${entry.id}/discard`, {});
    } catch {
      /* ignore — the object is one-time and will be swept anyway */
    }

    return { blob: new Blob([bytes], { type: contentType }), filename };
  }

  /** Trigger a browser "save as" for a decrypted blob. */
  function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return {
    uploadAttachment,
    listAttachments,
    downloadAttachment,
    saveBlob,
    buildAttachmentFragment,
    parseAttachmentFragment,
  };
}
